import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
} from '@mui/material';
import { jobAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';

const CustomerJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page] = useState(1);
  const [search, setSearch] = useState('');
  const { addToast } = useToast();

  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page };
      if (search) params.search = search;

      const response = await jobAPI.getMyJobs();
      const allJobs = response.data.data;
      
      const filteredJobs = allJobs.filter((job: any) =>
        job.title.toLowerCase().includes(search.toLowerCase()) ||
        job.description.toLowerCase().includes(search.toLowerCase())
      );
      
      setJobs(filteredJobs);
    } catch (err) {
      addToast('Failed to load jobs', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, search, addToast]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Typography variant="h4" component="h1" mb={3}>
        Meine Jobs
      </Typography>

      <Box mb={3}>
        <TextField
          placeholder="Jobs nach Titel oder Beschreibung durchsuchen..."
          value={search}
          onChange={handleSearchChange}
          size="small"
          fullWidth
        />
      </Box>

      {jobs.length === 0 ? (
        <Typography>No jobs submitted yet.</Typography>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>{job.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={
                        job.status === 'published'
                          ? 'success'
                          : job.status === 'rejected'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{job.category?.name || 'N/A'}</TableCell>
                  <TableCell>{job.budget ? `€${job.budget}` : 'N/A'}</TableCell>
                  <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </PortalLayout>
  );
};

export default CustomerJobsPage;
